import React, { useState } from 'react';
import './SmsTestView.css';

interface SmsTestResponse {
  success: boolean;
  message: string;
  timestamp?: string;
  phoneNumber?: string;
  sentMessage?: string;
  title?: string;
}

interface SmsStatusResponse {
  connectionTest: boolean;
  message: string;
  timestamp?: string;
}

const SmsTestView: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('[나주시 축산악취 알림]');
  const [lmsMessage, setLmsMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [smsResult, setSmsResult] = useState<SmsTestResponse | null>(null);
  const [lmsResult, setLmsResult] = useState<SmsTestResponse | null>(null);
  const [statusResult, setStatusResult] = useState<SmsStatusResponse | null>(null);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8090';

  // SMS 발송 테스트
  const handleSendSms = async () => {
    if (!phoneNumber.trim()) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    setSmsResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-test/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          message: message || undefined
        })
      });

      const result: SmsTestResponse = await response.json();
      setSmsResult(result);

    } catch (error) {
      console.error('SMS 발송 오류:', error);
      setSmsResult({
        success: false,
        message: 'SMS 발송 요청 중 오류가 발생했습니다.'
      });
    } finally {
      setLoading(false);
    }
  };

  // LMS 발송 테스트
  const handleSendLms = async () => {
    if (!phoneNumber.trim()) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    setLmsResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-test/send-lms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          title: title,
          message: lmsMessage || undefined
        })
      });

      const result: SmsTestResponse = await response.json();
      setLmsResult(result);

    } catch (error) {
      console.error('LMS 발송 오류:', error);
      setLmsResult({
        success: false,
        message: 'LMS 발송 요청 중 오류가 발생했습니다.'
      });
    } finally {
      setLoading(false);
    }
  };

  // SMS 서비스 상태 확인
  const handleCheckStatus = async () => {
    setLoading(true);
    setStatusResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-test/status`);
      const result: SmsStatusResponse = await response.json();
      setStatusResult(result);

    } catch (error) {
      console.error('SMS 상태 확인 오류:', error);
      setStatusResult({
        connectionTest: false,
        message: 'SMS 상태 확인 요청 중 오류가 발생했습니다.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sms-test-container">
      <div className="sms-test-header">
        <h1>SMS 발송 테스트</h1>
        <p>가비아 SMS 서비스 테스트 페이지입니다.</p>
      </div>

      <div className="sms-test-content">
        {/* 공통 입력 필드 */}
        <div className="input-section">
          <h3>공통 설정</h3>
          <div className="input-group">
            <label htmlFor="phoneNumber">받는 사람 전화번호 *</label>
            <input
              type="text"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="예: 010-1234-5678"
              disabled={loading}
            />
          </div>
        </div>

        {/* SMS 발송 섹션 */}
        <div className="sms-section">
          <h3>SMS 발송 테스트</h3>
          <div className="input-group">
            <label htmlFor="smsMessage">메시지 (비워두면 기본 메시지 사용)</label>
            <textarea
              id="smsMessage"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="SMS 메시지를 입력하세요. (최대 90자)"
              maxLength={90}
              rows={3}
              disabled={loading}
            />
            <small>{message.length}/90자</small>
          </div>
          <button
            onClick={handleSendSms}
            disabled={loading || !phoneNumber.trim()}
            className="send-button"
          >
            {loading ? '발송 중...' : 'SMS 발송'}
          </button>

          {smsResult && (
            <div className={`result ${smsResult.success ? 'success' : 'error'}`}>
              <h4>SMS 발송 결과</h4>
              <p><strong>상태:</strong> {smsResult.success ? '성공' : '실패'}</p>
              <p><strong>메시지:</strong> {smsResult.message}</p>
              {smsResult.timestamp && <p><strong>시간:</strong> {smsResult.timestamp}</p>}
              {smsResult.sentMessage && <p><strong>발송 메시지:</strong> {smsResult.sentMessage}</p>}
            </div>
          )}
        </div>

        {/* LMS 발송 섹션 */}
        <div className="lms-section">
          <h3>LMS 발송 테스트</h3>
          <div className="input-group">
            <label htmlFor="lmsTitle">제목</label>
            <input
              type="text"
              id="lmsTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="LMS 제목"
              disabled={loading}
            />
          </div>
          <div className="input-group">
            <label htmlFor="lmsMessage">메시지 (비워두면 기본 메시지 사용)</label>
            <textarea
              id="lmsMessage"
              value={lmsMessage}
              onChange={(e) => setLmsMessage(e.target.value)}
              placeholder="LMS 메시지를 입력하세요. (최대 2000자)"
              maxLength={2000}
              rows={5}
              disabled={loading}
            />
            <small>{lmsMessage.length}/2000자</small>
          </div>
          <button
            onClick={handleSendLms}
            disabled={loading || !phoneNumber.trim()}
            className="send-button"
          >
            {loading ? '발송 중...' : 'LMS 발송'}
          </button>

          {lmsResult && (
            <div className={`result ${lmsResult.success ? 'success' : 'error'}`}>
              <h4>LMS 발송 결과</h4>
              <p><strong>상태:</strong> {lmsResult.success ? '성공' : '실패'}</p>
              <p><strong>메시지:</strong> {lmsResult.message}</p>
              {lmsResult.timestamp && <p><strong>시간:</strong> {lmsResult.timestamp}</p>}
              {lmsResult.title && <p><strong>제목:</strong> {lmsResult.title}</p>}
              {lmsResult.sentMessage && <p><strong>발송 메시지:</strong> {lmsResult.sentMessage}</p>}
            </div>
          )}
        </div>

        {/* 서비스 상태 확인 섹션 */}
        <div className="status-section">
          <h3>SMS 서비스 상태 확인</h3>
          <button
            onClick={handleCheckStatus}
            disabled={loading}
            className="status-button"
          >
            {loading ? '확인 중...' : '상태 확인'}
          </button>

          {statusResult && (
            <div className={`result ${statusResult.connectionTest ? 'success' : 'error'}`}>
              <h4>서비스 상태</h4>
              <p><strong>연결 상태:</strong> {statusResult.connectionTest ? '정상' : '실패'}</p>
              <p><strong>메시지:</strong> {statusResult.message}</p>
              {statusResult.timestamp && <p><strong>확인 시간:</strong> {statusResult.timestamp}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmsTestView;